import React, { useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { Plus, Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { CalendarSkeleton } from "../ui/Skeleton";

// ─── Trainer Color Palette ───────────────────────────────────────────────────
// Consistent per-trainer colors inspired by CoachTrackPro
const TRAINER_COLORS = [
  { bg: "bg-violet-500", bgLight: "bg-violet-500/15", text: "text-violet-400", border: "border-violet-500/30", dot: "bg-violet-400" },
  { bg: "bg-emerald-500", bgLight: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30", dot: "bg-emerald-400" },
  { bg: "bg-sky-500", bgLight: "bg-sky-500/15", text: "text-sky-400", border: "border-sky-500/30", dot: "bg-sky-400" },
  { bg: "bg-amber-500", bgLight: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30", dot: "bg-amber-400" },
  { bg: "bg-rose-500", bgLight: "bg-rose-500/15", text: "text-rose-400", border: "border-rose-500/30", dot: "bg-rose-400" },
  { bg: "bg-cyan-500", bgLight: "bg-cyan-500/15", text: "text-cyan-400", border: "border-cyan-500/30", dot: "bg-cyan-400" },
  { bg: "bg-pink-500", bgLight: "bg-pink-500/15", text: "text-pink-400", border: "border-pink-500/30", dot: "bg-pink-400" },
  { bg: "bg-teal-500", bgLight: "bg-teal-500/15", text: "text-teal-400", border: "border-teal-500/30", dot: "bg-teal-400" },
  { bg: "bg-orange-500", bgLight: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30", dot: "bg-orange-400" },
  { bg: "bg-indigo-500", bgLight: "bg-indigo-500/15", text: "text-indigo-400", border: "border-indigo-500/30", dot: "bg-indigo-400" },
];

const getTrainerColor = (trainerId, trainerColorMap) => {
  return trainerColorMap[trainerId] || TRAINER_COLORS[0];
};

const SessionsCalendar = ({
  view,
  currentDate,
  sessions,
  loading,
  trainers,
  onSessionClick,
  onCreateSession,
}) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();

  // Popover state for monthly view day click
  const [popover, setPopover] = React.useState(null); // { date, events, x, y }
  const popoverRef = React.useRef(null);

  // Build consistent trainer → color mapping
  const trainerColorMap = useMemo(() => {
    const map = {};
    const uniqueTrainerIds = [...new Set(sessions.map((s) => s.trainerId))];
    uniqueTrainerIds.forEach((id, idx) => {
      map[id] = TRAINER_COLORS[idx % TRAINER_COLORS.length];
    });
    return map;
  }, [sessions]);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const [currentTime, setCurrentTime] = React.useState(moment());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const scrollRef = React.useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        const currentHour = moment().hour();
        const startHour = view === "weekly" ? 6 : 5;
        if (currentHour >= startHour) {
          const hourIndex = currentHour - startHour;
          scrollRef.current.scrollTop = hourIndex * 64 - 200;
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [view]);

  // Close popover on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopover(null);
      }
    };
    if (popover) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [popover]);

  // ─── Status styling ──────────────────────────────────────────────────────
  const getStatusStyle = (status) => {
    switch (status) {
      case "completed":
        return "bg-gradient-to-r from-emerald-500/90 to-green-600/90 text-white shadow-sm shadow-green-500/10";
      case "cancelled":
        return "bg-gradient-to-r from-zinc-600/80 to-zinc-700/80 text-gray-300 shadow-sm";
      case "no-show":
        return "bg-gradient-to-r from-red-500/90 to-rose-600/90 text-white shadow-sm shadow-red-500/10";
      default:
        return "bg-gradient-to-r from-blue-500/90 to-indigo-600/90 text-white shadow-sm shadow-blue-500/10";
    }
  };

  // ─── Get display name for a session ─────────────────────────────────────
  const getSessionDisplayName = useCallback((item) => {
    if (item.isGroupContainer) {
      const names = item.students.map((s) => s.studentName?.split(" ")[0]).join(", ");
      return names;
    }
    return item.studentName || "—";
  }, []);

  const getSessionTypeName = useCallback((item) => {
    return item.sessionTypeName || item.packageName || item.sessionType || "";
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // MONTHLY VIEW — CoachTrackPro-inspired compact pills
  // ═══════════════════════════════════════════════════════════════════════════
  const MonthlyCalendarView = () => {
    const monthStart = moment(currentDate).startOf("month");
    const monthEnd = moment(currentDate).endOf("month");
    const startDate = moment(monthStart).startOf("week");
    const endDate = moment(monthEnd).endOf("week");

    const weekdayHeaders = [];
    for (let i = 0; i < 7; i++) {
      weekdayHeaders.push(moment().weekday(i).format("ddd"));
    }

    const rows = [];
    let days = [];
    let day = moment(startDate);
    let safety = 0;

    while (day.isSameOrBefore(endDate, "day") && safety < 100) {
      for (let i = 0; i < 7; i++) {
        const clonedDay = moment(day);
        const formattedDate = clonedDay.format("YYYY-MM-DD");
        const isCurrentMonth = clonedDay.month() === currentDate.month();
        const dayEvents = sessions.filter(
          (item) => moment(item.start).format("YYYY-MM-DD") === formattedDate,
        );

        days.push({ date: clonedDay, isCurrentMonth, events: dayEvents, dayOfMonth: clonedDay.date() });
        day.add(1, "day");
      }
      rows.push(days);
      days = [];
      safety++;
    }

    const MAX_VISIBLE_PILLS = 3;

    const handleDayClick = (dayData, e) => {
      if (dayData.events.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setPopover({
        date: dayData.date,
        events: dayData.events,
        x: rect.left + rect.width / 2,
        y: rect.bottom + 4,
      });
    };

    return (
      <div className="relative">
        <div className={`overflow-hidden rounded-2xl border ${colors.tableBorder} shadow-xl`}>
          {/* Day Headers */}
          <div className={`grid grid-cols-7 ${colors.tableHeader}`}>
            {weekdayHeaders.map((dayName, idx) => (
              <div key={idx} className={`py-3 text-center text-xs font-bold uppercase tracking-wider ${colors.textSecondary} border-r last:border-r-0 ${colors.tableBorder}`}>
                {dayName}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          {rows.map((week, i) => (
            <div key={i} className={`grid grid-cols-7 border-t ${colors.tableBorder}`}>
              {week.map((dayData, idx) => {
                const isToday = moment().isSame(dayData.date, "day");
                const visibleEvents = dayData.events.slice(0, MAX_VISIBLE_PILLS);
                const overflowCount = dayData.events.length - MAX_VISIBLE_PILLS;

                return (
                  <div
                    key={idx}
                    className={`relative min-h-[72px] sm:min-h-[120px] p-1 sm:p-1.5 border-r last:border-r-0 ${colors.tableBorder} transition-colors cursor-pointer hover:bg-white/[0.03] ${
                      dayData.isCurrentMonth ? colors.cardBg : `${colors.inputBg} opacity-50`
                    } ${isToday ? "ring-1 ring-inset ring-blue-500/30 bg-blue-500/5" : ""}`}
                    onClick={(e) => handleDayClick(dayData, e)}
                  >
                    {/* Day Number */}
                    <div className="flex justify-between items-center mb-1.5">
                      <span className={`text-xs font-bold ${isToday ? "bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center" : dayData.isCurrentMonth ? colors.textPrimary : colors.textMuted}`}>
                        {dayData.dayOfMonth}
                      </span>
                      {dayData.isCurrentMonth && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onCreateSession(dayData.date.toDate()); }}
                          className={`p-0.5 rounded-md opacity-0 group-hover:opacity-100 hover:opacity-100 ${colors.bgHover} transition-all`}
                        >
                          <Plus className={`h-3 w-3 ${colors.textTertiary}`} />
                        </button>
                      )}
                    </div>

                    {/* Session Pills */}
                    <div className="flex flex-col gap-[3px]">
                      {visibleEvents.map((item) => {
                        const tc = getTrainerColor(item.trainerId, trainerColorMap);
                        const displayName = getSessionDisplayName(item);
                        const typeName = getSessionTypeName(item);

                        return (
                          <div
                            key={item.id}
                            onClick={(e) => { e.stopPropagation(); onSessionClick(item); }}
                            className={`flex items-center gap-1 px-1.5 py-[3px] rounded-md ${tc.bgLight} border ${tc.border} hover:brightness-125 transition-all cursor-pointer group/pill truncate`}
                          >
                            {item.isGroupContainer && (
                              <Users className={`w-2.5 h-2.5 ${tc.text} shrink-0 opacity-70`} />
                            )}
                            <span className={`text-[10px] font-semibold ${tc.text} truncate leading-tight`}>
                              {displayName}
                            </span>
                            <span className={`text-[9px] ${tc.text} opacity-50 truncate leading-tight hidden xl:inline`}>
                              {typeName}
                            </span>
                          </div>
                        );
                      })}

                      {overflowCount > 0 && (
                        <span className={`text-[10px] font-bold ${colors.textTertiary} pl-1.5 cursor-pointer hover:underline`}>
                          +{overflowCount} {t("sessionsPage.calendar.more", "daha")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* ─── Day Popover ─── */}
        {popover && (
          <div
            ref={popoverRef}
            className={`fixed z-50 w-80 max-w-[calc(100vw-1.5rem)] max-h-96 overflow-y-auto rounded-2xl ${colors.cardBg} border ${colors.tableBorder} shadow-2xl shadow-black/40 backdrop-blur-xl`}
            style={{
              left: Math.max(
                12,
                Math.min(popover.x - 160, window.innerWidth - Math.min(340, window.innerWidth - 12)),
              ),
              top: Math.min(popover.y, window.innerHeight - 400),
            }}
          >
            <div className={`sticky top-0 flex items-center justify-between p-3 border-b ${colors.tableBorder} ${colors.cardBg} backdrop-blur-xl z-10`}>
              <h4 className={`text-sm font-bold ${colors.textPrimary}`}>
                {popover.date.format("D MMMM YYYY dddd")}
              </h4>
              <button onClick={() => setPopover(null)} className={`p-1 rounded-lg ${colors.bgHover}`}>
                <X className={`w-4 h-4 ${colors.textSecondary}`} />
              </button>
            </div>
            <div className="p-2 flex flex-col gap-1">
              {/* Group by time */}
              {Object.entries(
                popover.events.reduce((acc, ev) => {
                  const timeKey = moment(ev.start).format("HH:mm") + " - " + moment(ev.end).format("HH:mm");
                  if (!acc[timeKey]) acc[timeKey] = [];
                  acc[timeKey].push(ev);
                  return acc;
                }, {}),
              ).sort(([a], [b]) => a.localeCompare(b)).map(([timeRange, events]) => (
                <div key={timeRange} className={`rounded-xl border ${colors.tableBorder} overflow-hidden`}>
                  <div className={`px-3 py-1.5 ${colors.tableHeader} border-b ${colors.tableBorder}`}>
                    <span className={`text-[11px] font-bold ${colors.textSecondary} tracking-wide`}>{timeRange}</span>
                  </div>
                  <div className="p-1 flex flex-col gap-0.5">
                    {events.map((ev) => {
                      const tc = getTrainerColor(ev.trainerId, trainerColorMap);
                      return (
                        <div
                          key={ev.id}
                          onClick={() => { onSessionClick(ev); setPopover(null); }}
                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg ${tc.bgLight} hover:brightness-125 cursor-pointer transition-all`}
                        >
                          <div className={`w-1.5 h-8 rounded-full ${tc.bg} shrink-0`} />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={`text-xs font-bold ${colors.textPrimary} truncate`}>
                              {getSessionDisplayName(ev)}
                            </span>
                            <span className={`text-[10px] ${colors.textSecondary} truncate`}>
                              {getSessionTypeName(ev)}
                            </span>
                          </div>
                          {ev.isGroupContainer && (
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${tc.bgLight} ${tc.text} border ${tc.border}`}>
                              <Users className="w-3 h-3 inline mr-0.5" />{ev.groupSize}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WEEKLY VIEW — Cleaner time grid with trainer-colored session cards
  // ═══════════════════════════════════════════════════════════════════════════
  const WeeklyCalendarView = () => {
    const weekStart = moment(currentDate).startOf("week");
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      weekDays.push(moment(weekStart).add(i, "days"));
    }

    const timeSlots = [];
    for (let hour = 6; hour < 22; hour++) {
      timeSlots.push(hour);
    }

    return (
      <div className="overflow-hidden flex flex-col h-[70dvh] min-h-[420px] md:h-[650px]">
        <div ref={scrollRef} className="overflow-y-auto custom-scrollbar flex-1 relative">
          {/* Header Row */}
          <div className={`grid grid-cols-[42px_repeat(7,minmax(0,1fr))] md:grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b ${colors.tableBorder} sticky top-0 z-20 ${colors.cardBg} backdrop-blur-xl`}>
            <div className={`py-3 px-1 text-[10px] font-bold ${colors.textSecondary} border-r ${colors.tableBorder} flex items-center justify-center uppercase tracking-wider`}>
              {t("sessionsPage.calendar.time")}
            </div>
            {weekDays.map((day) => {
              const isToday = moment().isSame(day, "day");
              return (
                <div key={day.format("YYYY-MM-DD")} className={`py-2 px-1 text-center border-r last:border-r-0 ${colors.tableBorder} ${isToday ? "bg-blue-500/10" : ""}`}>
                  <div className={`text-[10px] sm:text-xs font-medium uppercase tracking-wider ${colors.textSecondary}`}>
                    {day.format("ddd")}
                  </div>
                  <div className={`text-base sm:text-lg font-bold mt-0.5 ${isToday ? "bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto" : colors.textPrimary}`}>
                    {day.format("D")}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Grid */}
          {timeSlots.map((hour) => {
            const isCurrentHour = currentTime.hour() === hour;
            const topPosition = isCurrentHour ? (currentTime.minute() / 60) * 100 : 0;

            return (
              <div key={hour} className={`grid grid-cols-[42px_repeat(7,minmax(0,1fr))] md:grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b ${colors.tableBorder} min-h-[64px] relative`}>
                {/* Current time indicator */}
                {isCurrentHour && (
                  <div className="absolute z-20 w-full pointer-events-none flex items-center" style={{ top: `${topPosition}%` }}>
                    <div className="w-[42px] md:w-[56px] flex justify-end pr-1 shrink-0">
                      <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg shadow-red-500/30 leading-none">
                        {currentTime.format("HH:mm")}
                      </span>
                    </div>
                    <div className="flex-1 h-[2px] bg-red-500 shadow-sm shadow-red-500/50" />
                  </div>
                )}

                {/* Time Label */}
                <div className={`py-2 px-1 text-[10px] sm:text-xs font-bold ${colors.textMuted} text-center border-r ${colors.tableBorder} flex items-start justify-center pt-1`}>
                  {moment().hour(hour).minute(0).format("HH:mm")}
                </div>

                {/* Day Columns */}
                {weekDays.map((day) => {
                  const dayFormatted = day.format("YYYY-MM-DD");
                  const dayEvents = sessions.filter((item) => {
                    const sessionStart = moment(item.start);
                    return sessionStart.format("YYYY-MM-DD") === dayFormatted && sessionStart.hour() === hour;
                  });

                  return (
                    <div key={`${dayFormatted}-${hour}`} className={`relative p-[2px] border-r last:border-r-0 ${colors.tableBorder} group`}>
                      {/* Click targets */}
                      <div className="absolute inset-0 flex flex-col z-0">
                        <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(day.clone().hour(hour).minute(0).toDate())} />
                        <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(day.clone().hour(hour).minute(30).toDate())} />
                      </div>

                      {/* Session Cards */}
                      <div className="relative z-10 pointer-events-none flex flex-col gap-[2px]">
                        {dayEvents.map((item) => (
                          <div key={item.id} className="pointer-events-auto">
                            {renderWeeklySessionCard(item)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Weekly Session Card ──────────────────────────────────────────────────
  const renderWeeklySessionCard = (item) => {
    const tc = getTrainerColor(item.trainerId, trainerColorMap);
    const timeText = moment(item.start).format("HH:mm");

    if (item.isGroupContainer) {
      return (
        <div
          onClick={(e) => { e.stopPropagation(); onSessionClick(item); }}
          className={`flex flex-col p-1.5 rounded-lg border ${tc.border} ${tc.bgLight} hover:brightness-125 cursor-pointer transition-all`}
        >
          <div className="flex items-center gap-1 mb-0.5">
            <Users className={`w-3 h-3 ${tc.text} shrink-0`} />
            <span className={`text-[10px] font-black ${tc.text} uppercase leading-none`}>
              {t("sessionsPage.calendar.group", "GRUP")}
            </span>
            <span className={`text-[9px] px-1 py-[1px] rounded ${tc.bg} text-white font-bold leading-none ml-auto`}>
              {item.groupSize}
            </span>
          </div>
          {/* Student names - compact */}
          <div className="flex flex-wrap gap-0.5 mt-0.5">
            {item.students.slice(0, 4).map((s, idx) => (
              <span key={idx} className={`text-[8.5px] font-semibold ${tc.text} opacity-80 truncate max-w-[55px]`}>
                {s.studentName?.split(" ")[0]}{idx < Math.min(item.students.length, 4) - 1 ? "," : ""}
              </span>
            ))}
            {item.groupSize > 4 && (
              <span className={`text-[8px] font-bold ${tc.text} opacity-50`}>+{item.groupSize - 4}</span>
            )}
          </div>
          {/* Footer */}
          <div className="flex justify-between items-center mt-1 pt-1 border-t border-white/5">
            <span className={`text-[9px] font-semibold ${tc.text} opacity-60 truncate`}>
              {item.trainerName?.split(" ")[0]}
            </span>
            <span className={`text-[9px] font-black ${tc.text}`}>{timeText}</span>
          </div>
        </div>
      );
    }

    // Solo session card
    return (
      <div
        onClick={(e) => { e.stopPropagation(); onSessionClick(item); }}
        className={`flex flex-col p-1.5 rounded-lg ${getStatusStyle(item.status)} hover:brightness-110 cursor-pointer transition-all`}
      >
        <span className="text-[10px] font-bold leading-tight truncate">
          {item.studentName}
        </span>
        <span className="text-[8.5px] font-medium text-white/70 truncate mt-[1px]">
          {getSessionTypeName(item)}
        </span>
        <div className="flex justify-between items-center mt-1 pt-1 border-t border-white/10">
          <span className="text-[8.5px] font-semibold text-white/70 truncate">
            {item.trainerName?.split(" ")[0]}
          </span>
          <span className="text-[9px] font-black text-white/90">{timeText}</span>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // DAILY VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  const DailyCalendarView = () => {
    const dayStart = moment(currentDate).startOf("day");
    const timeSlots = [];
    for (let hour = 5; hour < 24; hour++) {
      timeSlots.push(moment().hour(hour).minute(0));
    }

    const dayFormatted = currentDate.format("YYYY-MM-DD");
    const dayEvents = sessions.filter(
      (item) => moment(item.start).format("YYYY-MM-DD") === dayFormatted,
    );

    return (
      <div className="overflow-hidden">
        <div className={`p-4 border-b ${colors.tableBorder} ${colors.tableHeader} flex items-center justify-between`}>
          <h3 className={`text-lg font-bold ${colors.textPrimary}`}>
            {currentDate.format("dddd, D MMMM YYYY")}
          </h3>
          <button
            onClick={() => onCreateSession(currentDate.toDate())}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold shadow-lg shadow-blue-500/20 text-sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("sessionsPage.addSession")}
          </button>
        </div>

        <div ref={scrollRef} className="max-h-[70dvh] md:max-h-[700px] overflow-y-auto custom-scrollbar">
          {timeSlots.map((timeSlot) => {
            const timeFormatted = timeSlot.format("HH:mm");
            const isCurrentHour = currentTime.hour() === timeSlot.hour();
            const topPosition = isCurrentHour ? (currentTime.minute() / 60) * 100 : 0;

            const slotEvents = dayEvents.filter((item) => {
              return moment(item.start).format("HH:mm") === timeFormatted;
            });

            return (
              <div key={timeFormatted} className={`flex border-b ${colors.tableBorder} min-h-[56px] group relative`}>
                {isCurrentHour && (
                  <div className="absolute z-20 w-full pointer-events-none flex items-center" style={{ top: `${topPosition}%` }}>
                    <div className="w-[42px] md:w-[56px] flex justify-end pr-1 shrink-0">
                      <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg shadow-red-500/30 leading-none">
                        {currentTime.format("HH:mm")}
                      </span>
                    </div>
                    <div className="flex-1 h-[2px] bg-red-500" />
                  </div>
                )}
                <div className={`w-[42px] md:w-[56px] py-3 px-1 text-[10px] md:text-xs font-bold ${colors.textMuted} border-r ${colors.tableBorder} flex-shrink-0 flex items-start justify-center pt-1`}>
                  {timeFormatted}
                </div>
                <div className="flex-1 relative group">
                  <div className="absolute inset-0 flex flex-col z-0">
                    <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(dayStart.clone().hour(timeSlot.hour()).minute(0).toDate())} />
                    <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(dayStart.clone().hour(timeSlot.hour()).minute(30).toDate())} />
                  </div>
                  <div className="relative z-10 pointer-events-none p-1.5 flex flex-wrap gap-2">
                    {slotEvents.map((item) => (
                      <div key={item.id} className="pointer-events-auto w-full max-w-sm">
                        {renderWeeklySessionCard(item)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // THREE-DAY VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  const ThreeDayCalendarView = () => {
    const days = [];
    for (let i = 0; i < 3; i++) {
      days.push(moment(currentDate).add(i, "days"));
    }

    const timeSlots = [];
    for (let hour = 6; hour < 22; hour++) {
      timeSlots.push(hour);
    }

    return (
      <div className="overflow-hidden flex flex-col h-[70dvh] min-h-[420px] md:h-[650px]">
        <div ref={scrollRef} className="overflow-y-auto custom-scrollbar flex-1 relative">
          {/* Header */}
          <div className={`grid grid-cols-[42px_repeat(3,minmax(0,1fr))] md:grid-cols-[56px_repeat(3,minmax(0,1fr))] border-b ${colors.tableBorder} sticky top-0 z-20 ${colors.cardBg} backdrop-blur-xl`}>
            <div className={`py-3 px-1 text-[10px] font-bold ${colors.textSecondary} border-r ${colors.tableBorder} flex items-center justify-center uppercase tracking-wider`}>
              {t("sessionsPage.calendar.time")}
            </div>
            {days.map((day) => {
              const isToday = moment().isSame(day, "day");
              return (
                <div key={day.format("YYYY-MM-DD")} className={`py-2 px-2 text-center border-r last:border-r-0 ${colors.tableBorder} ${isToday ? "bg-blue-500/10" : ""}`}>
                  <div className={`text-xs font-medium uppercase tracking-wider ${colors.textSecondary}`}>
                    {day.format("ddd")}
                  </div>
                  <div className={`text-lg font-bold mt-0.5 ${isToday ? "bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto" : colors.textPrimary}`}>
                    {day.format("D")}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Grid */}
          {timeSlots.map((hour) => {
            const isCurrentHour = currentTime.hour() === hour;
            const topPosition = isCurrentHour ? (currentTime.minute() / 60) * 100 : 0;

            return (
              <div key={hour} className={`grid grid-cols-[42px_repeat(3,minmax(0,1fr))] md:grid-cols-[56px_repeat(3,minmax(0,1fr))] border-b ${colors.tableBorder} min-h-[64px] relative`}>
                {isCurrentHour && (
                  <div className="absolute z-20 w-full pointer-events-none flex items-center" style={{ top: `${topPosition}%` }}>
                    <div className="w-[42px] md:w-[56px] flex justify-end pr-1 shrink-0">
                      <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg shadow-red-500/30 leading-none">
                        {currentTime.format("HH:mm")}
                      </span>
                    </div>
                    <div className="flex-1 h-[2px] bg-red-500" />
                  </div>
                )}
                <div className={`py-2 px-1 text-[10px] sm:text-xs font-bold ${colors.textMuted} text-center border-r ${colors.tableBorder} flex items-start justify-center pt-1`}>
                  {moment().hour(hour).minute(0).format("HH:mm")}
                </div>
                {days.map((day) => {
                  const dayFormatted = day.format("YYYY-MM-DD");
                  const dayEvents = sessions.filter((item) => {
                    const sessionStart = moment(item.start);
                    return sessionStart.format("YYYY-MM-DD") === dayFormatted && sessionStart.hour() === hour;
                  });

                  return (
                    <div key={`${dayFormatted}-${hour}`} className={`relative p-[2px] sm:p-1 border-r last:border-r-0 ${colors.tableBorder} group`}>
                      <div className="absolute inset-0 flex flex-col z-0">
                        <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(day.clone().hour(hour).minute(0).toDate())} />
                        <div className="flex-1 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onCreateSession(day.clone().hour(hour).minute(30).toDate())} />
                      </div>
                      <div className="relative z-10 pointer-events-none flex flex-col gap-[2px]">
                        {dayEvents.map((item) => (
                          <div key={item.id} className="pointer-events-auto">
                            {renderWeeklySessionCard(item)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return <CalendarSkeleton view={view} />;
  }

  // Render appropriate view
  switch (view) {
    case "daily":
      return <DailyCalendarView />;
    case "three_day":
      return <ThreeDayCalendarView />;
    case "weekly":
      return <WeeklyCalendarView />;
    case "monthly":
    default:
      return <MonthlyCalendarView />;
  }
};

export default SessionsCalendar;
